import React, { useEffect, useState } from 'react';
import styles from './CreditCard.module.css';

export interface CreditCardProps {
  /**
   * The monetary limit or balance to display.
   * @default 100000.00
   */
  limit?: number;
  /**
   * The last four digits of the card number.
   * @default '9841'
   */
  lastFourDigits?: string;
  /**
   * Whether the card is in an active state (pink) or inactive (gray).
   * @default true
   */
  isActive?: boolean;
  /**
   * Whether the card is blocked. Shows a lock overlay with animation.
   * @default false
   */
  isBlocked?: boolean;
  /**
   * Label text displayed above the amount.
   * @default 'Límite'
   */
  label?: string;
  /**
   * Optional click handler
   */
  onClick?: () => void;
}

export const CreditCard: React.FC<CreditCardProps> = ({
  limit,
  lastFourDigits,
  isActive,
  isBlocked,
  label = 'Límite',
  onClick
}) => {
  const [showOverlay, setShowOverlay] = useState(isBlocked);
  const [isUnlocking, setIsUnlocking] = useState(false);

  // Manage overlay visibility and animation states
  useEffect(() => {
    if (isBlocked) {
      // Blocking: Show overlay immediately, ensure not unlocking
      setShowOverlay(true);
      setIsUnlocking(false);
    } else if (showOverlay) {
      // Unblocking: If overlay was shown, trigger unlock animation
      setIsUnlocking(true);
      const timer = setTimeout(() => {
        setShowOverlay(false);
        setIsUnlocking(false);
      }, 600); // 0.3s for shackle + 0.3s for fade out
      return () => clearTimeout(timer);
    }
  }, [isBlocked, showOverlay]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Determine shackle class based on state
  const getShackleClass = () => {
    if (isUnlocking) return styles.shackleUnlocking;
    if (isBlocked) return styles.shackleLocking;
    return styles.shackleOpen;
  };

  return (
    <div
      className={`${styles.card} ${isActive ? styles.cardActive : styles.cardInactive}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-disabled={isBlocked}
    >
      {/* Background ellipses */}
      <div className={styles.bottomEllipse} />
      <div className={styles.topEllipse} />

      {/* Blocked Overlay */}
      {showOverlay && (
        <div className={`${styles.overlay} ${isUnlocking ? styles.overlayOut : ''}`}>
          <svg
            className={styles.lockIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <defs>
              <clipPath id="lock-shackle-clip">
                <rect x="0" y="0" width="24" height="11" />
              </clipPath>
            </defs>
            <path
              d="M7 11V7a5 5 0 0 1 10 0v4"
              className={getShackleClass()}
              clipPath="url(#lock-shackle-clip)"
            />
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          </svg>
        </div>
      )}

      {/* Content */}
      <div className={styles.content}>
        {/* Top section */}
        <div>
          <div className={styles.label}>{label}</div>
          <div className={styles.amount}>{formatCurrency(limit || 0)}</div>
        </div>

        {/* Mastercard logo */}
        <div className={styles.mastercardContainer}>
          <div className={styles.mastercardLogo}>
            <span className={`${styles.leftCircle} ${isActive ? styles.activeCircle : styles.inactiveCircle}`} />
            <span className={`${styles.rightCircle} ${isActive ? styles.activeCircle : styles.inactiveCircle}`} />
          </div>
          <span className={styles.mastercardText}>mastercard</span>
        </div>

        {/* Bottom section - Card number */}
        <div className={styles.cardNumber}>
          <div className={styles.dotsContainer}>
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
            <span className={styles.dot} />
          </div>
          <span>{lastFourDigits}</span>
        </div>
      </div>
    </div>
  );
};

export default CreditCard;
